import { FileType } from "../../../types/File";
import { DEFAULT_FILE_URL } from "../../../fetch/const.routes";
import { FILE_PATH } from "../../../const/enviroment";
import { useLayoutEffect, useRef, useState } from "react";
import Icon from "./Icon.tsx";
export function getLowResFileURL(file: FileType) {
  const baseName = `${DEFAULT_FILE_URL}/${FILE_PATH}${file.basename}`;
  if (!file.sizes) {
    return `${baseName}.${file.ext}`;
  }
  return `${baseName}-${file.sizes[0]}.${file.ext}`;
}

export function getHighResFileURL(file: FileType) {
  const baseName = `${DEFAULT_FILE_URL}/${FILE_PATH}${file.basename}`;
  if (!file.sizes) {
    return `${baseName}.${file.ext}`;
  }
  return `${baseName}-${file.sizes[file.sizes.length - 1]}.${file.ext}`;
}

export function getFileURLClosestTo(file: FileType, width: number) {
  const baseName = `${DEFAULT_FILE_URL}/${FILE_PATH}${file.basename}`;
  if (!file.sizes) {
    return `${baseName}.${file.ext}`;
  }
  let distance = Infinity;
  let size = 0;
  file.sizes.forEach((value) => {
    if (distance > Math.abs(width - value)) {
      size = value;
      distance = Math.abs(width - value);
    }
  });
  return `${baseName}-${size}.${file.ext}`;
}

export type ImageProps = {
  className?: string;
  alt: string;
  src: string | FileType;
  sizes?: string;
  loading?: "lazy" | "eager";
  pureMode?: boolean;
  iconSize?: number;
};

const Image = ({
  className,
  src,
  alt,
  sizes,
  loading = "lazy",
  pureMode = false,
  iconSize = 0,
}: ImageProps) => {
  const [highResLoaded, setHighResLoaded] = useState(false);
  const highResRef = useRef<HTMLImageElement>(null);
  useLayoutEffect(() => {
    if (highResRef.current?.complete) {
      setHighResLoaded(true);
    }
  }, []);
  if (typeof src === "string") {
    return <img className={className} src={src} alt={alt} loading={loading} />;
  }

  if (src.ext === "svg" && iconSize > 0) {
    return <Icon src={src} size={iconSize} />;
  }

  if (src.local) {
    return (
      <figure className={`${className ?? ""} relative overflow-hidden`}>
        <img
          className="absolute inset-0 w-full h-full object-cover"
          src={src.basename}
          alt={alt}
          loading={loading}
        />
        {/* same spacer as the high-res path */}
        <div aria-hidden="true" className="invisible w-full aspect-square" />
      </figure>
    );
  }
  const baseSrc = `${DEFAULT_FILE_URL}/${FILE_PATH}${src.basename}`;
  const cSrc = `${baseSrc}.${src.ext}`;

  if (!src.sizes) {
    return <img className={className} src={cSrc} alt={alt} loading={loading} />;
  }

  //Transform provided sizes to the string for **srcSet**
  const srcSet = src.sizes
    .map((size) => `${baseSrc}-${size}.${src.ext} ${size}w`)
    .join(", ");

  if (loading === "eager" || src.sizes.length < 2 || pureMode) {
    return (
      <img
        className={className}
        src={cSrc}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        loading={loading}
      />
    );
  }
  // Image.tsx — fix the figure/absolute layout
  const lowResSrc = `${baseSrc}-${src.sizes[0]}.${src.ext}`;
  return (
    <figure className={`${className ?? ""} relative overflow-hidden`}>
      <img
        src={lowResSrc}
        alt={`Platzhalter: ${alt}`}
        aria-hidden="true"
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
          highResLoaded ? "opacity-0" : "opacity-50"
        }`}
        loading="lazy"
      />
      <img
        ref={highResRef}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
          highResLoaded ? "opacity-100 blur-none" : "opacity-0 blur-lg"
        }`}
        src={cSrc}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        loading={loading}
        onLoad={() => setHighResLoaded(true)}
      />
      {/* Invisible spacer so the figure has height */}
      <div
        aria-hidden="true"
        style={{ paddingBottom: "100%" }}
        className="invisible w-full"
      />
    </figure>
  );
};
export default Image;
